import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHero from "@/components/public/PageHero";
import EmptyState from "@/components/public/EmptyState";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import { useLang } from "@/context/LangContext";
import { Warehouse as WarehouseIcon, Boxes, ClipboardCheck, PackageCheck, Container } from "lucide-react";

const SECTIONS = [
  { key: "WAREHOUSE", label: "Warehouse", icon: WarehouseIcon, desc: "Dry, controlled storage where green coffee is kept in stable conditions." },
  { key: "STORAGE", label: "Storage", icon: Boxes, desc: "Palletized burlap and GrainPro sacks organized by lot and grade." },
  { key: "QUALITY_CHECK", label: "Quality Check", icon: ClipboardCheck, desc: "Moisture, defect and screen size inspection before preparation." },
  { key: "PACKAGING", label: "Packaging", icon: PackageCheck, desc: "Coffee packed to agreed formats and prepared for shipment." },
  { key: "LOADING", label: "Loading", icon: Container, desc: "Coordination of trucking and container loading for export." },
];

export default function Warehouse() {
  const { t } = useLang();
  const [media, setMedia] = useState([]);

  useEffect(() => {
    (async () => { try { const r = await api.get("/warehouse-media"); setMedia(r.data); } catch (e) {} })();
  }, []);

  const bysection = (k) => media.filter((m) => m.section === k && m.image);

  return (
    <div>
      <PageHero eyebrow="Our Facility" title="Inside Warehouse Coffee" sub="Reliable coffee supply begins with proper storage, preparation and control." />
      <section className="mx-auto max-w-[1400px] space-y-20 px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        {SECTIONS.map((s, i) => {
          const imgs = bysection(s.key);
          return (
            <Reveal key={s.key}>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-forest text-offwhite"><s.icon className="h-5 w-5" /></div>
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber-brand">0{i+1}</span>
                  </div>
                  <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-forest-dark sm:text-3xl">{s.label}</h2>
                  <p className="mt-3 font-body text-base leading-relaxed text-espresso/70">{s.desc}</p>
                </div>
                <div className="lg:col-span-8">
                  {imgs.length === 0 ? (
                    <EmptyState message={t("photos.empty")} icon={s.icon} testId={`warehouse-empty-${s.key.toLowerCase()}`} />
                  ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {imgs.map((m) => (
                        <figure key={m.id} className="overflow-hidden rounded-xl border border-forest/10 bg-white">
                          <img src={m.image} alt={m.title || s.label} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                          {(m.title || m.description) && <figcaption className="p-4"><div className="font-display text-sm font-bold text-forest-dark">{m.title}</div><div className="mt-1 font-body text-xs text-espresso/60">{m.description}</div></figcaption>}
                        </figure>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>
      <CTASection />
    </div>
  );
}
